import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ShieldCheck, House, Folder, Bell, CloudArrowDown,
  ClockCounterClockwise, FileText, Users, SignOut,
  CaretLeft, CaretRight, NewspaperClipping, Buildings
} from '@phosphor-icons/react';

// ─── Feature toggles ─────────────────────────────────────────────
// Set to true to re-show the Organizations tab in the sidebar. The route,
// component, backend API, and MongoDB collection remain fully functional
// underneath — this only hides the nav entry from view.
// Set to true to re-show the sidebar Sign Out button. The header dropdown
// Sign Out remains the primary logout entry point; button code kept intact.
const SHOW_SIDEBAR_SIGNOUT = false;
const SHOW_ORGANIZATIONS_TAB = false;

const navItems = [
  { path: '/', label: 'Dashboard', icon: House },
  ...(SHOW_ORGANIZATIONS_TAB ? [{ path: '/organizations', label: 'Organizations', icon: Buildings }] : []),
  { path: '/accounts', label: 'Accounts', icon: Buildings },
  { path: '/cases', label: 'Cases', icon: Folder },
  { path: '/monitoring', label: 'Monitoring', icon: Bell },
  { path: '/media-alerts', label: 'Media Alerts', icon: NewspaperClipping },
  { path: '/data-sources', label: 'Data Sources', icon: CloudArrowDown },
  { path: '/audit', label: 'Audit Trail', icon: ClockCounterClockwise },
  { path: '/reports', label: 'Reports', icon: FileText },
];

const adminItems = [
  { path: '/users', label: 'User Management', icon: Users },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close user dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [userMenuOpen]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-[#0052FF] text-white'
        : 'text-[#52525B] hover:bg-[#F0F0F1] hover:text-[#0A0A0A]'
    }`;

  return (
    <div className="flex h-screen bg-[#F7F7F8]" data-testid="app-layout">
      <aside
        className={`${collapsed ? 'w-[68px]' : 'w-[240px]'} bg-white border-r border-[#E4E4E7] flex flex-col transition-all duration-200 shrink-0`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2 px-4 h-16 border-b border-[#E4E4E7] shrink-0">
          <ShieldCheck size={28} weight="duotone" className="text-[#0D7377] shrink-0" />
          {!collapsed && (
            <div>
              <span className="font-heading text-xl font-bold tracking-[3px]" style={{color: '#1B2A4A'}}>RADAR</span>
              <p className="text-[9px] text-[#0D7377] -mt-0.5">by Tychi</p>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.path === '/'} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
              <item.icon size={20} weight="regular" className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
          {user?.role === 'admin' && (
            <>
              <div className={`${collapsed ? 'mx-2' : 'mx-3'} my-3 h-px bg-[#E4E4E7]`} />
              {adminItems.map(item => (
                <NavLink key={item.path} to={item.path} className={linkClass} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <item.icon size={20} weight="regular" className="shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-[#E4E4E7] p-2 space-y-1 shrink-0">
          {SHOW_SIDEBAR_SIGNOUT && (
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#52525B] hover:bg-red-50 hover:text-red-600 w-full transition-all" data-testid="logout-btn">
              <SignOut size={20} className="shrink-0" />
              {!collapsed && <span>Sign Out</span>}
            </button>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#A1A1AA] hover:text-[#0A0A0A] w-full transition-all" data-testid="collapse-sidebar-btn">
            {collapsed ? <CaretRight size={18} /> : <><CaretLeft size={18} /><span>Collapse</span></>}
          </button>
          {!collapsed && (
            <div className="px-3 pt-1 text-center">
              <p className="text-[9px] text-[#A1A1AA]">Fundtec Services LLP</p>
              <p className="text-[9px] text-[#A1A1AA]">v2.0</p>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-[#E4E4E7] flex items-center justify-between px-6 shrink-0" data-testid="top-bar">
          <div />
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen(o => !o)}
              className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-[#F0F0F1] transition-colors"
              data-testid="user-menu-trigger"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <div className="text-right">
                <p className="text-sm font-medium text-[#0A0A0A]">{user?.name}</p>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">{user?.role}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-[#0052FF] flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
            </button>

            {userMenuOpen && (
              <div
                role="menu"
                data-testid="user-menu-dropdown"
                className="absolute right-0 mt-2 w-64 bg-white border border-[#E4E4E7] rounded-md shadow-lg py-1 z-50"
              >
                <div className="px-4 py-3 border-b border-[#E4E4E7]" data-testid="user-menu-identity">
                  <p className="text-sm font-medium text-[#0A0A0A] leading-tight">
                    {user?.name}
                    {user?.role && (
                      <>
                        <span className="text-[#A1A1AA] mx-1.5">—</span>
                        <span className="text-xs uppercase tracking-wider text-[#0D7377] font-semibold">{user.role}</span>
                      </>
                    )}
                  </p>
                  {user?.email && (
                    <p className="text-xs text-[#A1A1AA] mt-0.5 truncate" title={user.email}>{user.email}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#52525B] hover:bg-red-50 hover:text-red-600 transition-colors text-left"
                  data-testid="user-menu-signout"
                  role="menuitem"
                >
                  <SignOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          {children}
          <div className="mt-8 pt-3 border-t border-[#E4E4E7] text-center">
            <p className="text-[11px] text-[#999]">Radar AML Platform v2.0 | © 2026 Fundtec Services LLP | Radar and Tychi are products of Fundtec Services LLP</p>
          </div>
        </div>
      </main>
    </div>
  );
}
